import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Button,
  CircularProgress, Divider, Stepper, Step, StepLabel,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  DischargeDisposition, DischargePatient, PHASE_ORDER,
} from '~/types/discharge';
import { useDischargeContext } from '~/hooks/discharge/useDischargeContext';


// ── Clinical-needs vocabulary (subset of Coordinate's set) ──
type ClinicalNeed =
  | 'pt_ot' | 'wound_care' | 'iv_therapy' | 'medication_management'
  | 'skilled_nursing' | 'speech_therapy' | 'cardiac_monitoring';

const CLINICAL_NEED_LABELS: Record<ClinicalNeed, string> = {
  pt_ot: 'PT/OT',
  wound_care: 'Wound Care',
  iv_therapy: 'IV Therapy',
  medication_management: 'Medication Mgmt',
  skilled_nursing: 'Skilled Nursing',
  speech_therapy: 'Speech Therapy',
  cardiac_monitoring: 'Cardiac Monitoring',
};
const ALL_NEEDS = Object.keys(CLINICAL_NEED_LABELS) as ClinicalNeed[];

function defaultNeedsFor(disposition: DischargeDisposition): ClinicalNeed[] {
  switch (disposition) {
    case 'home_health':       return ['skilled_nursing', 'medication_management'];
    case 'hospital_at_home':  return ['skilled_nursing', 'iv_therapy', 'cardiac_monitoring'];
    case 'snf_at_home':       return ['skilled_nursing', 'pt_ot', 'medication_management'];
    case 'hospice':           return ['skilled_nursing', 'medication_management'];
    case 'infusion_at_home':  return ['iv_therapy', 'skilled_nursing'];
    case 'palliative':        return ['skilled_nursing', 'medication_management'];
  }
}

import { buildPlaceMatches, PlaceProviderMatch } from './buildPlaceMatches';

const FIT_STYLE: Record<PlaceProviderMatch['fitLabel'], { bgcolor: string; color: string }> = {
  'Best Fit':      { bgcolor: '#e0f7f4', color: '#006e60' },
  'Strong Option': { bgcolor: '#e5f6fb', color: '#153873' },
  'Backup':        { bgcolor: '#f6f6f6', color: '#668098' },
};

// ── Provider card ──
const ProviderCard: React.FC<{
  match: PlaceProviderMatch;
  rank: number;
  selected: boolean;
  selectable: boolean;
  onSelect: () => void;
}> = ({ match, rank, selected, selectable, onSelect }) => {
  const capacityLabel = match.capacity === 'high' ? 'High Capacity' : match.capacity === 'medium' ? 'Moderate Capacity' : 'Limited Capacity';
  const capacityColor = match.capacity === 'high'
    ? { color: '#006e60', borderColor: '#00bda1' }
    : match.capacity === 'medium'
      ? { color: '#153873', borderColor: 'rgba(21,56,115,0.4)' }
      : { color: '#b71c1c', borderColor: 'rgba(183,28,28,0.4)' };
  const clinicalFitPct = Math.round(match.clinicalFit);
  const fitChipColor = clinicalFitPct >= 80
    ? { color: '#006e60', borderColor: '#00bda1' }
    : clinicalFitPct >= 50
      ? { color: '#153873', borderColor: 'rgba(21,56,115,0.4)' }
      : { color: '#668098', borderColor: 'rgba(102,128,152,0.4)' };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? '#00bda1' : 'divider',
        bgcolor: selected ? 'rgba(0,189,161,0.04)' : 'transparent',
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              #{rank + 1}. {match.name}
            </Typography>
            {match.preferredPartner && (
              <Chip label="Preferred Partner" size="small" variant="outlined"
                sx={{ fontSize: '0.72rem', height: 20, fontWeight: 600, color: '#635f99', borderColor: 'rgba(99,95,153,0.5)' }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">{match.distanceMiles} mi away</Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={match.fitLabel} size="small"
            sx={{ fontWeight: 700, fontSize: '0.72rem', height: 20, ...FIT_STYLE[match.fitLabel] }} />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Score: {match.totalScore}
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
        <Chip
          icon={<CheckCircleIcon sx={{ color: '#00bda1 !important', fontSize: '0.9rem !important' }} />}
          label="Insurance Match" size="small" variant="outlined"
          sx={{ color: '#006e60', borderColor: '#00bda1' }}
        />
        <Chip label={capacityLabel} size="small" variant="outlined" sx={capacityColor} />
        <Chip label={`Clinical Fit ${clinicalFitPct}%`} size="small" variant="outlined" sx={fitChipColor} />
        <Chip
          icon={<StarIcon sx={{ fontSize: '0.85rem !important' }} />}
          label={match.qualityRating.toFixed(1)}
          size="small" variant="outlined"
        />
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {selected ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CheckCircleIcon sx={{ color: '#00bda1' }} fontSize="small" />
          <Typography variant="body2" sx={{ color: '#006e60' }} fontWeight={600}>
            Confirmed — Ready referral
          </Typography>
        </Stack>
      ) : selectable && (
        <Button
          variant={match.fitLabel === 'Best Fit' ? 'contained' : 'outlined'}
          size="small"
          onClick={onSelect}
        >
          Select & Confirm
        </Button>
      )}
    </Paper>
  );
};

// ── Section orchestrator ──
type Stage = 'setup' | 'matching' | 'confirming' | 'matched' | 'completed';

interface Props {
  patient: DischargePatient;
}

const PlaceSection: React.FC<Props> = ({ patient }) => {
  const { setPhase, setSelectedProvider, setDeclinedProvider, setNoResponseProvider } = useDischargeContext();
  const placeAlreadyDone = PHASE_ORDER.indexOf(patient.phase) > PHASE_ORDER.indexOf('place');
  const matches = useMemo(() => buildPlaceMatches(patient), [patient]);

  const [needs, setNeeds] = useState<ClinicalNeed[]>(() => defaultNeedsFor(patient.disposition));
  const [stage, setStage] = useState<Stage>(placeAlreadyDone ? 'completed' : 'setup');
  const initialSelectedId = placeAlreadyDone
    ? (matches.find(m => m.name === patient.selectedProviderName)?.id ?? matches[0]?.id ?? null)
    : null;
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId);

  const toggleNeed = (n: ClinicalNeed) => {
    setNeeds(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
  };

  const runMatching = () => {
    setStage('matching');
    setTimeout(() => setStage('matched'), 1500);
  };

  const onSelectAndConfirm = (id: string) => {
    setSelectedId(id);
    setStage('confirming');
    setTimeout(() => {
      const match = matches.find(m => m.id === id);
      if (match) setSelectedProvider(patient.id, match.name);
      // Clear any prior decline / no-response notice now that a new provider is in flight.
      setDeclinedProvider(patient.id, undefined);
      setNoResponseProvider(patient.id, undefined);
      setStage('completed');
      setPhase(patient.id, 'refer');
    }, 1200);
  };

  // ── Stage views ──
  if (stage === 'setup') {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, maxWidth: 480 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Clinical Needs
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {ALL_NEEDS.map(need => {
              const selected = needs.includes(need);
              return (
                <Chip
                  key={need}
                  label={CLINICAL_NEED_LABELS[need]}
                  size="small"
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => toggleNeed(need)}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Button variant="contained" fullWidth onClick={runMatching}>
          Run Matching
        </Button>
      </Paper>
    );
  }

  if (stage === 'matching') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 2 }}>
        <CircularProgress size={36} />
        <Typography variant="h6" color="text.secondary">Evaluating providers…</Typography>
        <Typography variant="body2" color="text.secondary">
          Checking insurance, geography, clinical fit, and acceptance likelihood.
        </Typography>
      </Box>
    );
  }

  if (stage === 'confirming') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 220, gap: 3 }}>
        <CircularProgress size={36} />
        <Stepper activeStep={0} alternativeLabel sx={{ width: '100%', maxWidth: 400 }}>
          <Step><StepLabel>Confirming availability</StepLabel></Step>
          <Step><StepLabel>Assembling referral packet</StepLabel></Step>
        </Stepper>
        <Typography variant="body2" color="text.secondary">
          Checking {matches.find(m => m.id === selectedId)?.name ?? 'provider'} availability…
        </Typography>
      </Box>
    );
  }

  if (stage === 'matched') {
    return (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Matched Providers</Typography>
            <Typography variant="caption" color="text.secondary">
              {matches.length} providers ranked by placement likelihood
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {matches.map((m, idx) => (
            <ProviderCard
              key={m.id}
              match={m}
              rank={idx}
              selected={false}
              selectable
              onSelect={() => onSelectAndConfirm(m.id)}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // completed
  const selected = matches.find(m => m.id === selectedId) ?? matches[0];
  if (!selected) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.68rem', letterSpacing: 0.6, display: 'block', mb: 1 }}>
        Selected Provider
      </Typography>
      <ProviderCard match={selected} rank={0} selected selectable={false} onSelect={() => undefined} />
    </Box>
  );
};

export default PlaceSection;
